import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import Button from "../components/Button";

/*
 Invariant:
 - All date fields in models are native JS Date objects
 - Luxon DateTime is used ONLY for date arithmetic/comparison
*/

const toDate = (d) => DateTime.fromJSDate(d);

const dayDiff = (endDate, nextStartDate) => {
    const end = toDate(endDate).startOf("day");
    const next = toDate(nextStartDate).startOf("day");
    return next.diff(end, "days").days;
};

const colorsMatch = (a, b, rules) => {
    if (rules.background && a.colors.background !== b.colors.background) {
        return false;
    }
    if (rules.border && a.colors.border !== b.colors.border) {
        return false;
    }
    if (rules.text && a.colors.text !== b.colors.text) {
        return false;
    }
    return true;
};

const notesMatch = (a, b, rules) => {
    const n1 = a.note || "";
    const n2 = b.note || "";

    if (!rules.noteMode) {
        return true;
    }

    if (rules.noteMode === "same") {
        return n1 === n2;
    }

    if (!rules.noteValue || rules.noteValue.trim() === "") {
        return false;
    }

    const value = rules.noteValue;

    switch (rules.noteMode) {
        case "prefix":
            return n1.startsWith(value) && n2.startsWith(value);
        case "suffix":
            return n1.endsWith(value) && n2.endsWith(value);
        default:
            return true;
    }
};

const buildRanges = (items, rules, sortFirst, maxGapDays) => {
    if (items.length === 0) {
        return [];
    }

    const ordered = sortFirst
        ? [...items].sort(
            (a, b) =>
                toDate(a.startDate).toMillis() -
                toDate(b.startDate).toMillis()
        )
        : items;

    const groups = [];
    let current = [ordered[0]];

    for (let i = 1; i < ordered.length; i++) {
        const prev = current[current.length - 1];
        const next = ordered[i];

        const gap = dayDiff(prev.endDate, next.startDate);

        const canMerge =
            gap >= 1 &&
            gap <= maxGapDays + 1 &&
            colorsMatch(prev, next, rules) &&
            notesMatch(prev, next, rules);

        if (canMerge) {
            current.push(next);
        } else {
            groups.push(current);
            current = [next];
        }
    }

    groups.push(current);
    return groups;
};

const formatDate = (d) => DateTime.fromJSDate(d).toISODate();

const distinctColors = (group, key) => {
    const seen = new Set();
    const result = [];

    for (const item of group) {
        const value = item.colors[key] ?? null;
        if (!seen.has(value)) {
            seen.add(value);
            result.push(value);
        }
    }

    return result;
};

const ColorSwatch = ({ color, borderColor }) => (
    <div
        className="w-5 h-5 rounded border"
        style={{
            backgroundColor: color || "transparent",
            borderColor: borderColor || "#ccc"
        }}
    />
);

const ColorOption = ({
    color,
    name,
    checked,
    onChange,
    showRadio
}) => (
    <label className="flex items-center gap-2">
        <ColorSwatch color={color} borderColor={color} />
        {showRadio && (
            <input
                type="radio"
                name={name}
                checked={checked}
                onChange={onChange}
            />
        )}
    </label>
);

const CombineRangesModal = ({ dates, onConfirm, onCancel }) => {
    const selected = useMemo(
        () => dates.filter((d) => d.selected),
        [dates]
    );

    const [rules, setRules] = useState({
        background: true,
        border: false,
        text: false,
        noteMode: null,
        noteValue: ""
    });

    const [sortFirst, setSortFirst] = useState(true);
    const [maxGapDays, setMaxGapDays] = useState(0);

    const groups = useMemo(
        () => buildRanges(selected, rules, sortFirst, maxGapDays),
        [selected, rules, sortFirst, maxGapDays]
    );

    const showNoteValueField =
        rules.noteMode === "prefix" || rules.noteMode === "suffix";


    const [mergedColorSelection, setMergedColorSelection] = useState({});
    const [noteSelection, setNoteSelection] = useState({});

    useEffect(() => {
        setNoteSelection(prev => {
            const next = { ...prev };

            selected.forEach(item => {
                if (!(item.id in next)) {
                    next[item.id] = true;
                }
            });

            // Optional: remove entries for items no longer selected
            Object.keys(next).forEach(id => {
                if (!selected.some(item => item.id === id)) {
                    delete next[id];
                }
            });

            return next;
        });
    }, [selected]);

    useMemo(() => {
        const next = { ...mergedColorSelection };

        groups.forEach((group, groupIndex) => {
            if (!next[groupIndex]) {
                next[groupIndex] = {};
            }

            ["background", "border", "text"].forEach((key) => {
                if (next[groupIndex][key] === undefined) {
                    const values = distinctColors(group, key);
                    next[groupIndex][key] = values[0] ?? null;
                }
            });
        });

        setMergedColorSelection(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups/*, mergedColorSelection do not add! creates a endless cycle*/]);


    function renderMergableRow({ group, groupIndex, item, itemIndex, merged }) {
        const selection = mergedColorSelection[groupIndex];

        const mergedBackground = selection?.background ?? merged.colors.background;
        const mergedBorder = selection?.border ?? merged.colors.border;
        const mergedText = selection?.text ?? merged.colors.text;

        const selectedNotes = group.flatMap(item =>
            noteSelection[item.id] ? [item.note] : []
        );

        const mergedNote = selectedNotes.join(", ");

        const mergedStart = group[0].startDate;
        const mergedEnd = group[group.length - 1].endDate;

        const isFirstBg =
            group.findIndex(g => g.colors.background === item.colors.background) === itemIndex;

        const isFirstBorder =
            group.findIndex(g => g.colors.border === item.colors.border) === itemIndex;

        const isFirstText =
            group.findIndex(g => g.colors.text === item.colors.text) === itemIndex;

        return (
            <tr key={`${groupIndex}-${itemIndex}`}>
                {itemIndex === 0 && (
                    <>
                        <td
                            rowSpan={group.length}
                            className="border px-2 py-1 align-top font-medium"
                        >
                            {formatDate(mergedStart)}
                        </td>
                        <td
                            rowSpan={group.length}
                            className="border px-2 py-1 align-top font-medium"
                        >
                            {formatDate(mergedEnd)}
                        </td>
                    </>
                )}

                {/* Items sub-columns */}
                <td className="border px-2 py-1">{formatDate(item.startDate)}</td>
                <td className="border px-2 py-1">{formatDate(item.endDate)}</td>
                <td className="border px-2 py-1">
                    <ColorOption
                        color={item.colors.background}
                        name={`bg-${groupIndex}`}
                        showRadio={isFirstBg}
                        checked={selection && selection.background === item.colors.background}
                        onChange={() =>
                            setMergedColorSelection({
                                ...mergedColorSelection,
                                [groupIndex]: {
                                    ...selection,
                                    background: item.colors.background
                                }
                            })
                        }
                    />
                </td>
                <td className="border px-2 py-1">
                    <ColorOption
                        color={item.colors.border}
                        name={`border-${groupIndex}`}
                        showRadio={isFirstBorder}
                        checked={selection && selection.border === item.colors.border}
                        onChange={() =>
                            setMergedColorSelection({
                                ...mergedColorSelection,
                                [groupIndex]: {
                                    ...selection,
                                    border: item.colors.border
                                }
                            })
                        }
                    />
                </td>
                <td className="border px-2 py-1">
                    <ColorOption
                        color={item.colors.text}
                        name={`text-${groupIndex}`}
                        showRadio={isFirstText}
                        checked={selection && selection.text === item.colors.text}
                        onChange={() =>
                            setMergedColorSelection({
                                ...mergedColorSelection,
                                [groupIndex]: {
                                    ...selection,
                                    text: item.colors.text
                                }
                            })
                        }
                    />
                </td>
                <td className="border px-2 py-1 max-w-xs truncate">
                    <input
                        type="checkbox"
                        checked={noteSelection[item.id] ?? false}
                        onChange={(e) =>
                            setNoteSelection(prev => ({
                                ...prev,
                                [item.id]: e.target.checked
                            }))
                        }
                        className="mr-2"
                    />
                    {item.note || "—"}
                </td>

                {/* Merged columns on the right (rowSpan) */}
                {itemIndex === 0 && (
                    <>
                        <td
                            rowSpan={group.length}
                            className="border px-2 py-1 align-top"
                        >
                            <ColorSwatch color={mergedBackground} />
                        </td>
                        <td
                            rowSpan={group.length}
                            className="border px-2 py-1 align-top"
                        >
                            <ColorSwatch
                                color={mergedBorder}
                                borderColor={mergedBorder}
                            />
                        </td>
                        <td
                            rowSpan={group.length}
                            className="border px-2 py-1 align-top"
                        >
                            <ColorSwatch color={mergedText} />
                        </td>
                        <td
                            rowSpan={group.length}
                            className="border px-2 py-1 align-top max-w-xs"
                        >
                            {mergedNote || "—"}
                        </td>
                    </>
                )}
            </tr>
        );
    }

    function renderNonMergableRow({ groupIndex, merged }) {
        return (
            <tr key={groupIndex}>
                <td className="border px-2 py-1">
                    {formatDate(merged.startDate)}
                </td>
                <td className="border px-2 py-1">
                    {formatDate(merged.endDate)}
                </td>

                <td
                    colSpan={6}
                    className="border px-2 py-2 text-gray-600 italic text-center"
                >
                    Item cannot be merged
                </td>

                <td className="border px-2 py-1">
                    <ColorSwatch color={merged.colors.background} />
                </td>
                <td className="border px-2 py-1">
                    <ColorSwatch
                        color={merged.colors.border}
                        borderColor={merged.colors.border}
                    />
                </td>
                <td className="border px-2 py-1">
                    <ColorSwatch color={merged.colors.text} />
                </td>
                <td className="border px-2 py-1">
                    {merged.note || "—"}
                </td>
            </tr>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="inline-block bg-white rounded p-6 space-y-4 max-w-[90vw]">
                <h3 className="text-lg font-semibold">Combine date ranges</h3>
                <div className="flex items-center items-baseline gap-8">
                    <div className="space-y-2">
                        {/* Rules */}
                        <div className="space-y-2">
                            <div className="font-medium">Rules</div>

                            <label className="block">
                                <input
                                    type="checkbox"
                                    checked={rules.background}
                                    onChange={(e) =>
                                        setRules({ ...rules, background: e.target.checked })
                                    }
                                />
                                <span className="ml-2">Same background color</span>
                            </label>

                            <label className="block">
                                <input
                                    type="checkbox"
                                    checked={rules.border}
                                    onChange={(e) =>
                                        setRules({ ...rules, border: e.target.checked })
                                    }
                                />
                                <span className="ml-2">Same border color</span>
                            </label>

                            <label className="block">
                                <input
                                    type="checkbox"
                                    checked={rules.text}
                                    onChange={(e) =>
                                        setRules({ ...rules, text: e.target.checked })
                                    }
                                />
                                <span className="ml-2">Same text color</span>
                            </label>

                            <div className="flex items-center gap-2">
                                <span className="font-medium">Notes</span>

                                <select
                                    className="border rounded px-2 py-1"
                                    value={rules.noteMode || ""}
                                    onChange={(e) =>
                                        setRules({
                                            ...rules,
                                            noteMode: e.target.value || null,
                                            noteValue: ""
                                        })
                                    }
                                >
                                    <option value="">Ignore</option>
                                    <option value="same">Same</option>
                                    <option value="prefix">Prefix</option>
                                    <option value="suffix">Suffix</option>
                                </select>

                                {showNoteValueField && (
                                    <input
                                        type="text"
                                        className="border rounded px-2 py-1"
                                        placeholder="Suffix or Prefix"
                                        value={rules.noteValue}
                                        onChange={(e) =>
                                            setRules({ ...rules, noteValue: e.target.value })
                                        }
                                    />
                                )}
                            </div>
                        </div>

                        {/* Combine options */}
                        <div className="space-y-2">
                            <div className="font-medium">Combine options</div>

                            <label className="block">
                                <input
                                    type="checkbox"
                                    checked={sortFirst}
                                    onChange={(e) => setSortFirst(e.target.checked)}
                                />
                                <span className="ml-2">Sort by date before combining</span>
                            </label>

                            <label className="block">
                                <span className="mr-2">Allow gaps up to</span>
                                <input
                                    type="number"
                                    min={0}
                                    className="border rounded px-2 py-1 w-20"
                                    value={maxGapDays}
                                    onChange={(e) =>
                                        setMaxGapDays(Math.max(0, Number(e.target.value)))
                                    }
                                />
                                <span className="ml-2">days</span>
                            </label>
                        </div>
                    </div>

                    {/* Preview */}
                    <div>
                        <div className="font-medium mb-1">Preview</div>

                        <div style={{ minHeight: "200px", maxHeight: "70vh", overflowY: "scroll" }}>
                            <table className="table-auto border w-full text-sm">
                                <thead>
                                    <tr>
                                        <th className="border px-2 py-1">Start</th>
                                        <th className="border px-2 py-1">End</th>

                                        <th className="border px-2 py-1" colSpan={6}>
                                            Items
                                        </th>

                                        <th className="border px-2 py-1">Background</th>
                                        <th className="border px-2 py-1">Border</th>
                                        <th className="border px-2 py-1">Text</th>
                                        <th className="border px-2 py-1">Note</th>
                                    </tr>

                                    <tr>
                                        <th className="border px-2 py-1"></th>
                                        <th className="border px-2 py-1"></th>

                                        <th className="border px-2 py-1">Start</th>
                                        <th className="border px-2 py-1">End</th>
                                        <th className="border px-2 py-1">Background</th>
                                        <th className="border px-2 py-1">Border</th>
                                        <th className="border px-2 py-1">Text</th>
                                        <th className="border px-2 py-1">Note</th>

                                        <th className="border px-2 py-1"></th>
                                        <th className="border px-2 py-1"></th>
                                        <th className="border px-2 py-1"></th>
                                        <th className="border px-2 py-1"></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {groups.map((group, groupIndex) => {
                                        const merged = group[0];

                                        if (group.length === 1) {
                                            return renderNonMergableRow({ groupIndex, merged });
                                        }

                                        return group.map((item, itemIndex) => (
                                            renderMergableRow({ group, groupIndex, item, itemIndex, merged })
                                        ));
                                    })}

                                    {groups.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={12}
                                                className="border px-2 py-3 text-center text-gray-500"
                                            >
                                                No ranges can be combined with current settings
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" label="Cancel" onClick={onCancel} className="px-3 py-1" />
                    <Button variant="primary" label="Combine" onClick={() => onConfirm(groups)} disabled={groups.length === 0} className="px-3 py-1" />
                </div>
            </div>
        </div>
    );
};

export default CombineRangesModal;
